<template>
  <div v-if="isShow" :class="[$style.banner, color === '#fff' && $style.bold]" :style="styleObj">
    <a :href="href">
      <div v-if="isSale">
        <span :class="$style.sale">SALE</span>
      </div>
      <span
        >{{ title }} <template v-if="subtitle"><br />{{ subtitle }}</template></span
      >
      <span :class="$style.showMore">詳細はこちら</span>
    </a>
    <button type="button" :class="$style.close" aria-label="キャンペーンバナーを閉じる" @click="onClick" />
  </div>
</template>

<script setup lang="ts">
import jsSHA from 'jssha/dist/sha1';
import { ref, onMounted } from 'vue';
import { getFromStorage, setToStorage } from '@/js/lib/helper/expirable-local-storage';

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    color: string;
    backgroundColor: string;
    href: string;
    isSale: boolean;
  }>(),
  {
    subtitle: '',
  },
);
const TTL = 86400;
const styleObj = {
  '--background-color': props.backgroundColor,
  '--color': props.color,
};
const isShow = ref(true);
const genKey = (href: string) => {
  const sha1 = new jsSHA('SHA-1', 'TEXT');
  sha1.update(href);
  return `close-campaign-banner-${sha1.getHash('HEX')}`;
};
const key = genKey(props.href);
const onClick = () => {
  isShow.value = false;
  setToStorage({ key, value: true, expirationSeconds: TTL });
};

onMounted(() => {
  isShow.value = !getFromStorage({ key });
});
</script>

<style module lang="scss">
.banner {
  background-color: var(--background-color);
  display: block;
  -webkit-font-smoothing: antialiased;
  height: 48px;
  position: relative;
  text-align: center;

  @media (--pc) {
    margin: 0 min(calc(540px - 50vw), -20px);
  }

  a {
    align-items: center;
    color: var(--color-white);
    display: flex;
    font-size: 0;
    height: 100%;
    justify-content: center;
    line-height: 1.4;
    text-decoration: none;

    @media (--sp) {
      margin-left: -10px;
    }

    @media (--sp-mini) {
      margin-left: -20px;
    }

    .sale {
      background-color: var(--color-white);
      border-radius: 16px;
      color: var(--color-red);
      font-weight: bold;
      margin-right: 10px;
      padding: 5px 8px;
    }

    br {
      display: none;

      @media (--sp) {
        display: inline;
      }
    }

    .showMore {
      padding: 8px 5px;

      @media (--sp) {
        display: none;
      }
    }
  }

  &.bold a {
    font-weight: bold;
  }

  span {
    font-size: 14px;

    @media (--sp) {
      display: inline-block;
      font-size: 13px;
    }

    @media (--sp-mini) {
      font-size: 12px;
    }
  }

  .close {
    align-items: center;
    color: var(--color-white);
    content: $icon-cross1;
    cursor: pointer;
    display: flex;
    height: 48px;
    justify-content: center;
    position: absolute;
    right: 0;
    text-align: center;
    top: 0;
    width: 30px;

    &::before {
      color: var(--color-white);
      content: $icon-cross1;
      font-family: $font-icon;
      font-size: 16px;
    }
  }

  a,
  a button,
  .close,
  .close::before {
    color: var(--color);
  }
}
</style>
